import React, { useRef, useState } from 'react';
import { bandForPage } from '../nav/activeBand';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/LocaleContext';
import { SETTINGS_SECTIONS } from '../nav/settingsSections';
import { ADMIN_SECTIONS } from '../nav/adminSections';
import { visibleCockpitTabs } from '../nav/cockpitTabs';
import { useP } from '../i18n/useP';
import { ChevronLeft, ChevronRight, Diamond, Lightbulb, Lock, Settings } from 'lucide-react';
import { RiArrowLeftSLine, RiArrowRightSLine, RiBookOpenLine, RiCoinsLine, RiDashboard3Line, RiDownload2Line, RiGroupLine, RiHammerLine, RiHistoryLine, RiHome4Line, RiLogoutCircleRLine, RiPencilLine, RiProhibitedLine, RiPushpinLine, RiSettings3Line, RiShieldKeyholeLine, RiShoppingCart2Line, RiTimerLine, RiUnpinLine } from '@remixicon/react';
import Logo from './Logo';
import Listbox from './Listbox';
import './Sidebar.css';

/* ── Icônes : Lucide (`lucide-react`), seule bibliothèque d'icônes de Palestre
   (décision 2026-09-07, remplace les tracés SVG à la main). Le dessin vient de
   la bibliothèque ; taille, épaisseur de trait et couleur viennent de la classe
   `.sb-ic` (currentColor), jamais de l'icône elle-même. ──────────────────── */
/* Marqueur d'ENVIRONNEMENT, déduit du nom d'hôte (même build partout — la
   prod n'affiche rien) : dev.palestre.ch → DEV, localhost → LOCAL. */
export const ENV_TAG = (() => {
  const h = window.location.hostname;
  if (h.startsWith('dev.')) return 'DEV';
  if (h === 'localhost' || h === '127.0.0.1') return 'LOCAL';
  return null;
})();

/* Remix (formes pleines) est la bibliothèque des icônes du menu ; Lucide (trait)
   reste pour Savoirs, le cadenas et le losange, le temps de la migration. */
const ic = (Icon) => <Icon className="sb-ic" aria-hidden="true" />;
const icLucide = (Icon) => <Icon className="sb-ic sb-ic--stroke" aria-hidden="true" />;
const I = {
  // Maison : le Cockpit, dont le panneau ouvre sur l'Accueil (2026-09-16).
  welcome: ic(RiHome4Line),
  cockpit: ic(RiDashboard3Line),
  // Chronomètre : Analytique Services part de l'HEURE mesurée (2026-08-30).
  analytics: ic(RiTimerLine),
  people: ic(RiGroupLine),
  financials: ic(RiCoinsLine),
  sales: ic(RiShoppingCart2Line),
  // Boussole : Planification stratégique, le cap à moyen/long terme (2026-08-15).
  // Ampoule : Savoirs (fusionne Savoirs + Lexique dans son panneau).
  savoirs: icLucide(Lightbulb),
  settings: ic(RiSettings3Line),
  admin: ic(RiShieldKeyholeLine),
  audit: ic(RiHistoryLine),
  // Clé plate : Atelier, le coin de l'exploitant (2026-09-01).
  wip: ic(RiHammerLine),
  lexique: ic(RiBookOpenLine),
  download: ic(RiDownload2Line),
  logout: ic(RiLogoutCircleRLine),
};
/* Les icônes du rail servent aussi ailleurs (la barre supérieure porte les
   paramètres depuis le 2026-08-23) : un seul tracé, pas une copie. */
export const ICONS = I;

/* ── Configuration de navigation (mappée sur les clés de page existantes) ──
   Ordre des modules (décision 2026-09-18) : l'Accueil, puis le CHEMIN DE LA
   VALEUR — Finance (les comptes), Ventes & Ops (le revenu qui les alimente),
   People (ce qu'il coûte), Analytique (la marge qui se déduit des deux). Le
   socle financier reste en tête, comme depuis le 2026-07-22 (« ce qui tourne
   seul d'abord ») : le Cockpit est presque entièrement financier, et l'éloigner
   de Finance coûtait un aller-retour à chaque indicateur qu'on veut ouvrir.
   Analytique passe en DERNIER, à sa place : il consomme People et Finance. */
// ⚠️ Source de vérité de l'ordre du menu. `reportNav.js` en dérive NAV_SECTIONS
// (flèches ‹ ›) — ne pas redéfinir l'ordre ailleurs.
// Savoirs (l'ampoule) n'est plus une bande du menu depuis le 2026-09-16 : le
// bouton du rail ouvre le VOLET Savoirs (components/knowledge/KnowledgeDrawer),
// qui porte Guide · Fiches · Lexique sans passer par un panneau de sections.

/* LE GROUPE PLANIFICATION, IDENTIQUE DANS FINANCE ET DANS VENTES & OPS
   (décision Olivier, 2026-09-29) : mêmes sections, même ordre, même adresse
   de page des deux côtés. Il appartient à Finance ; Ventes & Ops le montre
   pour le dirigeant. L'ordre (2026-09-30) : ce qu'on engage (salaires,
   acquisitions), puis le plan de liquidité qui le lit, puis le Résultat
   prévisionnel, qui est la SORTIE de Finance. Les emprunts existants et les
   actifs existants vivent dans Finance › Données et réglages : ce qu'on
   simule en financement se saisit dans le plan de liquidité. Chacun
   n'y voit que ce que son rôle permet (le rôle Opérations : Investissements).
   Le menu reste dans le module d'où l'on vient (`nav/activeBand.js`). `lock`
   (Ventes & Ops seulement) dit ce qui dépend de Finance ; `needsModule` GRISE
   la section chez un client sans Finance, sans la retirer : le groupe garde la
   même structure partout (pas de résultat prévisionnel sans Finance). */
const planningGroup = (lock = false, ventes = false) => ({ headKey: ventes ? 'nav:scenarii.finance' : 'nav:planning', items: [
  /* ACQUISITIONS EN TÊTE ; LE FONDS DE ROULEMENT SUIT LE PLAN DE LIQUIDITÉ
     (2026-09-30) : créances, fournisseurs, acomptes, TVA et stocks réunis, que
     le plan lit en Pro. Il demande Finance, comme le Résultat prévisionnel. */
  /* Dans Ventes & Ops, les Acquisitions sont sous « Opérations » (2026-10-22). */
  ...(ventes ? [] : [{ labelKey: 'nav:items.financials-capex-plan', key: 'financials-capex-plan' }]),
  /* Dans Ventes & Ops le groupe s'appelle « Finance & RH » et la page des salaires, celle de Finance, s'y appelle « Salaires ». */
  { labelKey: ventes ? 'nav:items.fin-payroll-costs' : 'nav:items.fin-payroll', key: 'fin-payroll' },
  { labelKey: 'nav:items.forecast-wc', key: 'forecast-wc' },
  { labelKey: 'nav:items.fin-wc', key: 'fin-wc', needsModule: 'financials', ...(lock ? { lock: true } : {}) },
  { labelKey: 'nav:items.forecast-out', key: 'forecast-out', needsModule: 'financials', ...(lock ? { lock: true } : {}) },
  /* EN SUSPENS (2026-09-30) : visible, marqué « bientôt », la page le dit. */
  { labelKey: 'nav:items.business-plan', key: 'business-plan', needsModule: 'financials', tag: 'bientôt', ...(lock ? { lock: true } : {}) },
] });

export const NAV = [
  /* COCKPIT — l'entrée porte la MAISON depuis que l'Accueil y vit
     (2026-09-16) : son panneau ouvre sur « Accueil », la page que tout le
     monde a (nouveautés, guide à reprendre), puis les vues du Cockpit. Une
     seconde icône de rail pour l'Accueil doublait cette entrée. */
  { type: 'page', id: 'home', labelKey: 'nav:bands.home', icon: I.welcome },

  // 1 — Finance : reporting du RÉEL + prévisionnel + simulation cash 12M.
  // Séparation par métier (2026-08-09) : LIRE (hub à onglets, vue Réel /
  // Prévisionnel) · CONSTRUIRE (parcours d'étapes numérotées, même grammaire que
  // Planification) · SAISIR / RÉGLER. La construction du BILAN prévisionnel reste
  // pour l'instant dans le hub « Bilan & cash » : la découper en étapes demande
  // de repenser son écran, pas seulement de le router.
  { type: 'band', id: 'financials', labelKey: 'nav:bands.financials', icon: I.financials, groups: [
    /* LES ÉTATS EN ACCÈS DIRECT (2026-09-17) : chaque état financier est une
       SECTION, plus un onglet à chercher dans un hub « Rapports ». On ouvre le
       bilan d'un clic, et le menu dit enfin ce que le module contient. Les six
       sections sont servies par le MÊME composant (`FinancialsReportsHub`,
       drapeau `only`) : le contexte de lecture (vue Réel | Prévisionnel |
       Budget, arrêté, unité) est donc partagé au niveau du module et survit au
       changement d'écran. Le hub à onglets reste routé hors menu, le Cockpit y
       renvoyant par un relais.
       La SYNTHÈSE ouvre le module sous sa propre tête, « Vue de direction » :
       c'est la page où l'on arrive, et elle renvoie au Cockpit pour les vues de
       direction (Rentabilité, Liquidité & solidité, Financement, Focus+), qui y
       restent. */

    /* « États financiers » : ce qui se LIT, cinq états et rien d'autre.
       « Indicateurs financiers » réunit le BFRE et le ratio de liquidité sous
       un sélecteur : deux mesures du même cycle, deux sections n'auraient rien
       ajouté. Le PLAN DE LIQUIDITÉ arrive de la Trésorerie, qui garde ses
       supports de construction. */
    { headKey: 'finance:menu.read', items: [
      /* LA SYNTHÈSE OUVRE LES RAPPORTS (2026-09-18) : elle tenait son propre
         groupe, « Vue de direction », pour une seule entrée — une tête de
         groupe qui annonçait une ligne. Elle est le premier rapport, pas un
         genre à part. */
      { labelKey: 'nav:items.financials-synthesis',  key: 'financials-synthesis' },
      { labelKey: 'nav:items.financials-pl',         key: 'financials-pl' },
      { labelKey: 'nav:items.financials-bs',         key: 'financials-bs' },
      { labelKey: 'nav:items.financials-cf',         key: 'financials-cf' },
      /* LA LIQUIDITÉ PRÉVISIONNELLE a rejoint la Planification (2026-09-29),
         sous le nom du Plan de liquidité qu'elle était : sa page reste routée. */
      { labelKey: 'nav:items.financials-indicators', key: 'financials-indicators' },
      /* FOCUS+ rejoint Finance (2026-09-18), après les indicateurs : ses
         rapports sur-mesure se construisent sur les mêmes états, et le Cockpit
         n'a pas à porter un atelier d'analyse au milieu de ses vues. */
      { labelKey: 'nav:items.custom-reports',        key: 'custom-reports' },
    ]},
    /* LE PRÉVISIONNEL SE CONSTRUIT DANS FINANCE (décision Olivier, 2026-09-29,
       qui défait celle du 2026-09-27) : un client Finance seul planifie tout
       depuis son compte de résultat et quelques écrans spécifiques. */
    planningGroup(),
    // « Saisie & réglages » (fusion des deux groupes, 2026-08-19) : Données
    // (mettre à jour le réel — le Mapping en est un onglet depuis le
    // 2026-08-18) puis les réglages (sans marque ☀ depuis le 2026-08-20 —
    // le ton atténué `setting` suffit, mark: 'none' supprime l'icône).
    // Les hubs de supports sont dissous le même jour : Exploitation est devenu
    // les onglets de Trésorerie ; Investissement & financement s'est réparti
    // entre Prévisionnel (capex, mouvements exceptionnels) et Trésorerie
    // (financement). Leurs pages restent montées par ces hubs, en embedded.
    { headKey: 'finance:menu.data', items: [
      // `muted` = TON, pas rôle (2026-08-21) : Données reste une SAISIE, mais
      // dans ce groupe elle s'affichait plus sombre que Paramètres et
      // Structure, ce qui la faisait ressortir sans raison. Le groupe se lit
      // d'un seul ton ; sa nature est dite par la tête de groupe.
      { labelKey: 'nav:items.financials-data-hub',    key: 'financials-data-hub', muted: true },
      /* ACTIFS EXISTANTS et EMPRUNTS EXISTANTS (2026-09-30) : des contrats et
         des règles comptables qu'on pose une fois, visibles dans Finance seul. */
      { labelKey: 'nav:items.financials-assets',      key: 'financials-assets', muted: true },
      { labelKey: 'nav:items.financials-financing',   key: 'financials-financing', muted: true },
      /* EMPRUNTS ET APPORTS (2026-09-27) : les contrats existants et leur
         dénouement, une SAISIE de Finance que le plan de liquidité lit ; le
         besoin de financement se lit là-bas, du trou que l'exploitation laisse.
         PARC EXISTANT : les postes du bilan et leurs amortissements, que
         Finance continue de calculer. */
      /* Emprunts et apports et Parc existant sont partis à la Planification
         le 2026-09-29, le second dans les Investissements ; leurs pages
         restent routées. */
      /* PARAMÈTRES ET STRUCTURE SONT PARTIS AUX PARAMÈTRES DU CLIENT
         (2026-09-27) : la clôture, la TVA, les lignes de crédit et la
         structure des rapports sont lues par PLUSIEURS modules. Tant qu'elles
         vivaient dans Finance, un client sans Finance n'avait pas de plan
         comptable et ne pouvait rien imputer. Leurs pages restent routées. */
    ]},
  ]},

  // 2 — People : réorganisé le 2026-08-21 en TROIS GROUPES, dans l'ordre du
  // métier plutôt que dans l'ordre « coût vérifiable » de juillet. Le dirigeant
  // fait trois choses, dans cet ordre : il PILOTE (il planifie et concrétise),
  // il CALCULE (du salaire horaire au coût de capacité), et il ouvre le DÉTAIL
  // nominatif quand il en a besoin — donc en pied de menu.
  { type: 'band', id: 'scenarii', labelKey: 'nav:bands.scenarii', icon: I.sales, groups: [
    /* QUATRE GROUPES (décision Olivier, 2026-09-27) : le module PLANIFIE tout
       ce que l'entreprise gagne, dépense, investit et encaisse. VENTES, les
       trois sources de revenu qui se suivent (facturé, signé, prévu) après le
       catalogue et sa trajectoire ; OPÉRATIONS, ce qu'on achète, stocke et
       investit ; FINANCE, sous cadenas (périmètre `financials`), ce qui touche
       aux charges, au personnel et à la trésorerie, que le vendeur ne voit
       pas ; puis les suivis annexes. Le prévisionnel ne se construit plus dans
       Finance : il se lit là-bas, contre le réel. */
    { headKey: 'nav:scenarii.sales', items: [
      { labelKey: 'nav:items.sales-monthly', key: 'sales-monthly' },
      /* PRÉVISIONS DES VENTES (2026-10-09) : une section, trois vues
         (Portefeuille · Nouvelles ventes · Total), à la place de Portefeuille
         et Ventes prévisionnelles ; `sales-portfolio` reste routé. */
      { labelKey: 'nav:items.sales-backlog', key: 'sales-backlog' },
      /* LA TRAJECTOIRE APRÈS LES PRÉVISIONS (2026-10-14). */
      { labelKey: 'nav:items.sales-trajectory', key: 'sales-trajectory' },
    ]},
    /* OPÉRATIONS (2026-09-29 ; « Coûts » le 2026-10-22, redevenu « Opérations » le même jour) : les trois natures de charges,
       directes, variables, fixes, les acquisitions, puis les opérations spéciales. Les salaires sont revenus sous « Finance & RH » (2026-10-22). */
    { headKey: 'nav:scenarii.costs', items: [
      { labelKey: 'nav:items.fc-stock', key: 'fc-stock' },
      { labelKey: 'nav:items.fc-variable', key: 'fc-variable' },
      /* LE BUDGET DES CHARGES (décision Olivier, 2026-09-28) : une ligne par
         charge prévue, rattachée à un compte ; le Résultat prévisionnel et le
         plan de liquidité le lisent. */
      { labelKey: 'nav:items.fin-budget', key: 'fin-budget' },
      { labelKey: 'nav:items.financials-capex-plan', key: 'financials-capex-plan' },
      /* OPÉRATIONS SPÉCIALES (2026-10-20) : ce que le plan ne déduit d'aucune
         règle, avec ou sans impact sur la liquidité. Sorti du Plan de
         liquidité, qui garde la carte des mouvements de trésorerie. */
      { labelKey: 'nav:items.special-ops', key: 'special-ops' },
    ]},
    /* LA PLANIFICATION, réplique du groupe de Finance (2026-09-29), appelée « Finance & RH » ici (2026-10-22), avec les
       salaires dessous. Le plan de liquidité et les salaires fonctionnent sans Finance, en Manuel. */
    planningGroup(true, true),
    /* « DONNÉES & RÉGLAGES », comme en Finance (décision d'Olivier, 2026-10-22) : le réalisé collé depuis l'outil du
       client, puis les correspondances qui rattachent chacun de ses libellés à une offre. Le réalisé a quitté le groupe
       Ventes pour s'y ranger ; même ton atténué que le groupe de Finance. */
    { headKey: 'nav:scenarii.data', items: [
      /* MES OFFRES, le catalogue, se règle ici (demande d'Olivier, 2026-10-22). */
      { labelKey: 'nav:items.sales-offers-list', key: 'sales-offers-list', muted: true },
      { labelKey: 'nav:items.sales-billing', key: 'sales-billing', muted: true },
      /* CACHÉE DU MENU (demande d'Olivier, 2026-10-22) : on y accède par le bouton « Correspondances » de la page Revenus réalisés. */
      { labelKey: 'nav:items.sales-source-maps', key: 'sales-source-maps', muted: true, hidden: true },
      /* L'ANCIENNE PRÉVISION range ici (demande d'Olivier, 2026-10-22), toujours marquée du panneau interdit. */
      { labelKey: 'nav:items.sales-atterrissage', key: 'sales-atterrissage', muted: true, mark: 'ban' },
    ]},
  ]},

  // 6 — Chantier (id `wip`) : le module où mûrissent les écrans non finis
  // (décision 2026-08-26). `adminOnly` = RÉSERVÉ À L'EXPLOITANT, comme le
  // Journal : ce n'est PAS un module commercial. Il ne se coche nulle part, il
  // n'apparaît dans aucun pack client — sinon le client à qui on l'active
  // verrait justement ce qu'on cherche à lui cacher. Un écran s'y range le
  // temps de sa construction, puis rejoint son module définitif.
  // Le bouton du Chantier ne montre QUE « En construction » (2026-09-02) : on
  // peut vouloir y faire voir un écran en cours. « Exploitation » (Journal,
  // Nouveautés) est passé sous l'Administration. La bande garde ses deux
  // groupes : c'est elle qui porte la garde `adminOnly` de TOUTES ses pages —
  // sortir `treasury` d'ici la rouvrirait à tout client qui a Finance, et
  // `hr-time-pilot` à tout client qui a People (ces pages appartiennent à un
  // module, contrairement à `admin-hub` dont la garde est écrite en dur).
  { type: 'band', id: 'rh', labelKey: 'nav:bands.rh', icon: I.people, groups: [
    // Piloter — la Synthèse est l'accueil du module (où j'en suis), la
    // Planification l'outil de travail (où je vais). Elle a quitté le milieu
    // du menu : c'est un outil de pilotage, pas un rapport.
    { headKey: 'people:menu.pilot', items: [
      /* L'ACCUEIL ouvre le module (2026-09-01) : les portes de la mise à jour,
         puis le récit de l'année. */
      { labelKey: 'nav:items.hr-home',         key: 'hr-home' },
      /* L'EFFECTIF suit (2026-08-29) : avant de lire ce que coûte
         le personnel, on regarde qui il est. Le constaté et le planifié y sont
         côte à côte depuis le 2026-08-27 — ils vivaient dans deux sections
         différentes. */
      { labelKey: 'nav:items.hr-headcount',    key: 'hr-headcount' },
      /* Puis on CHIFFRE (Synthèse, qui porte aussi les deux évolutions depuis
         le 2026-08-21), puis on projette (Planification). */
      /* (« Synthèse » est devenue le PREMIER ONGLET de Chiffres clés le
         2026-08-27 : deux adresses voisines pour une seule lecture, on
         raconte puis on chiffre au même endroit. Sa page reste routée.) */
      { labelKey: 'nav:items.hr-synthese',     key: 'hr-synthese' },
      /* (« Détail des charges » est devenue un ONGLET de Charges le 2026-08-29 :
         elle zoome sur l'année que la section chiffre déjà. Sa page reste
         routée.) */
      { labelKey: 'nav:items.hr-planning',     key: 'hr-planning' },
      /* L'horizon s'élargit : après l'exercice chiffré, la trajectoire par
         département et l'évolution à cinq ans (2026-08-30). */
      { labelKey: 'nav:items.hr-trajectory',   key: 'hr-trajectory' },
    ]},
    // Détail — lecture ET saisie regroupées PAR SUJET (et non séparées comme
    // en Finance) : ici rien ne se présente à un conseil, c'est un outil de
    // travail. D'où « Détail » et non « Rapports », qui en Finance signifie
    // lecture seule et présentable.
    // Ordre du 2026-08-21 : on part de CE QU'ON EST (les contrats), on passe
    // par ce qu'ils déclenchent (les charges sociales), et on finit par la
    // lecture agrégée (les charges de personnel). Le stylo a quitté Contrats :
    // dans un groupe où tout se saisit, il ne distinguait rien — et il
    // décalait le libellé, seul de sa colonne à ne pas démarrer au même x.
    { headKey: 'people:menu.detail', items: [
      // 2026-08-21 soir : l'ex-section « Contrats » a cédé ses trois onglets
      // (Collaborateurs + Contrats → « Contrats et frais » ; Autres paramètres
      // → « Paramètres ») — vidée, elle disparaît. Données d'abord, règles
      // ensuite.
      /* Ordre de la SAISIE (2026-09-01) : ce qu'on VERSE, ce que cela
         DÉCLENCHE, ce qu'on REMBOURSE, puis le temps qui règle l'année. */
      { labelKey: 'nav:items.hr-personnel',    key: 'hr-personnel' },
      /* LA PRÉVOYANCE A SA SECTION (2026-09-27) : la LPP n'appartient qu'à
         People, elle est nominative ; les taux de charges sociales, eux, se
         lisent aussi dans les Paramètres du client. */
      { labelKey: 'nav:items.hr-lpp',          key: 'hr-lpp' },
      { labelKey: 'nav:items.hr-social-hub',   key: 'hr-social-hub' },
      { labelKey: 'nav:items.hr-expenses',     key: 'hr-expenses' },
      { labelKey: 'nav:items.hr-time-hub',     key: 'hr-time-hub' },
    ]},
  ]},

  /* 3 — Rentabilité : TOUTE la chaîne du coût de l'heure à la marge par
     service (2026-08-27). Temps standard, salaire horaire et coût horaire
     viennent de People, où ils formaient un groupe « Calculer » désormais
     dissous : on ne lit pas une chaîne de calcul à cheval sur deux modules.
     People garde ce qui touche AUX PERSONNES (contrats, charges, effectif) ;
     le calendrier y reste, c'est un réglage de l'entreprise. */
  { type: 'band', id: 'simulation', labelKey: 'nav:bands.simulation', icon: I.analytics, groups: [
    /* CALCULER : la chaîne, dans son ordre — le salaire de l'heure, le coût
       complet de l'heure, le coût de l'heure produite, la marge par service. */
    { headKey: 'rentabilite:menu.calc', items: [
      { labelKey: 'nav:items.hr-lcr-hub', key: 'hr-lcr-hub' },
      { labelKey: 'nav:items.cost-rates-hub', key: 'cost-rates-hub' },
      { labelKey: 'nav:items.chr-pcr', key: 'chr-pcr' },
      { labelKey: 'nav:items.tdabc-rentabilite-client', key: 'tdabc-rentabilite-client' },
    ]},
    /* SAISIES ET RÉGLAGES (2026-09-01) : ce que la chaîne consomme, dans un
       seul groupe. Il en a porté deux le temps d'une journée, quand la saisie
       du temps tenait deux sections ; elle n'en tient plus qu'une, et un
       groupe d'une entrée ne se justifiait plus.
       L'ordre reste celui de la mise à jour : on compte les heures, puis on
       pose les réglages que le calcul consomme. */
    { headKey: 'rentabilite:menu.data', items: [
      /* (« Temps par activité » est devenu le DERNIER ONGLET de « Saisie des
         heures » le 2026-09-01 : même saisie, vue par l'autre bout. Sa page
         reste routée.) */
      { labelKey: 'nav:items.time-standard', key: 'time-standard' },
      { labelKey: 'nav:items.indirect-items', key: 'indirect-items' },
      { labelKey: 'nav:items.cost-pools', key: 'cost-pools' },
      { labelKey: 'nav:items.rc-services', key: 'rc-services' },
    ]},
  ]},

  // 4 — Ventes & Opérations (S&OP) : les PLANS d'abord (produits · services ·
  // ventes = l'agrégé), puis les supports en ambre — même code couleur que
  // « Calculs et supports » de Finance (id `scenarii` = clé du pack commercial).
  { type: 'band', id: 'wip', adminOnly: true, railGroup: 'building', labelKey: 'nav:bands.wip', icon: I.wip, groups: [
    // Deux natures sous la même icône, d'où les groupes : l'outillage de
    // l'exploitant (permanent, ce qu'on vient consulter) puis ce qui n'est pas
    // fini (de passage, ça repart un jour). Une liste plate les confondrait, et
    // l'ordre dit lequel des deux est la raison d'ouvrir la bande.
    // Journal d'audit — rapatrié du pied de rail le 2026-08-26 : il y vivait en
    // icône solitaire, sans nom ni voisinage. Il ne relève d'aucun pack ni
    // d'aucun périmètre (il les traverse tous, salaires nominatifs compris),
    // donc sa place est ici, avec le reste de ce que l'exploitant seul voit.
    { id: 'ops', headKey: 'nav:wip.ops', items: [
      // Nouveautés — l'exploitant rédige le « Quoi de neuf » que les pages
      // d'accueil des modules affichent (2026-09-01). Permanent : de
      // l'outillage, pas un écran de passage. En tête depuis le 2026-09-02.
      { labelKey: 'nav:items.wip-news', key: 'wip-news' },
      { labelKey: 'audit:title', key: 'audit-log' },
    ]},
    { id: 'building', headKey: 'nav:wip.building', items: [
      { labelKey: 'nav:items.wip-home', key: 'wip-home' },
      // Simulation liquidité 12M — venue de Finance le 2026-08-26 : l'écran
      // n'est pas fini, et deux adresses de trésorerie côte à côte troublaient
      // la lecture. Rien d'autre ne bouge (même clé de page, mêmes routes
      // serveur) : c'est le MENU qui déplace l'écran, `adminOnly` qui le ferme.
      { labelKey: 'nav:items.treasury', key: 'treasury' },
      // Pilotage temps — les deux LECTURES du temps (vue d'ensemble, audit),
      // sorties du hub Temps le 2026-08-26 : Temps SAISIT, Pilotage temps LIT.
      // Ici le temps d'être finie. Sa clé garde le préfixe `hr-` et son
      // périmètre reste `people` : le jour où elle rejoint People, seul le
      // menu bougera.
      { labelKey: 'nav:items.hr-time-pilot', key: 'hr-time-pilot' },
      // Plan des ventes — les affaires (commandes, offres remises,
      // opportunités), venu de Ventes le 2026-09-12 le temps d'être repensé
      // avec le Portefeuille. Même clé, mêmes routes, périmètre `sales`.
      { labelKey: 'nav:items.sales-plan', key: 'sales-plan' },
      // 12 mois roulants — venu de Ventes le 2026-09-27 : le plan par
      // versions le remplace. Même clé, mêmes routes, périmètre `sales`.
      { labelKey: 'nav:items.sales-forecast', key: 'sales-forecast' },
    ]},
  ]},
];

// Settings — référentiel pur (réglages tenant, TVA, design, échange de données).
export const SETTINGS = { type: 'page', id: 'settings-hub', labelKey: 'nav:settings', icon: I.settings };

// Bande dont le RAIL ne montre qu'un groupe (`railGroup`) : les autres sont
// servis ailleurs. Aujourd'hui le Chantier, dont « Exploitation » (Journal,
// Nouveautés) est passé sous l'Administration.
function railGroupBand(nav) { return (nav || []).find(n => n.type === 'band' && n.railGroup) || null; }
// Les groupes de cette bande QUI NE SONT PAS au rail : ils s'affichent dans le
// panneau de l'Administration.
function opsGroups(nav) {
  const b = railGroupBand(nav);
  return b ? b.groups.filter(g => g.id !== b.railGroup) : [];
}
function opsKeys(nav) { return opsGroups(nav).flatMap(g => g.items.map(i => i.key)); }

/* LES LIEUX D'OÙ L'ON REVIENT (2026-09-18) : les réglages et les trois adresses
   de l'exploitant s'ouvrent depuis n'importe quel écran, et l'on veut retrouver
   celui qu'on a quitté. Ils ne s'atteignent pas par les modules, donc rien dans
   le menu n'y ramène. `App` retient la dernière page HORS de cette liste, le
   panneau la nomme et y renvoie. */
export function isAsideKey(nav, page) {
  return page === 'settings-hub' || page === 'admin-hub'
    || opsKeys(nav).includes(page);
}
// Les pages que le bouton de la bande ouvre (son seul groupe de rail).
function railGroupKeys(nav) {
  const b = railGroupBand(nav);
  if (!b) return [];
  const g = b.groups.find(x => x.id === b.railGroup);
  return g ? g.items.map(i => i.key) : [];
}

// Toutes les clés de page d'une bande (pour savoir si elle contient l'actif).
function bandKeys(band) {
  if (band.items) return band.items.filter(i => i.key).map(i => i.key);
  if (band.groups) return band.groups.flatMap(g => g.items.map(i => i.key));
  return [];
}

/* Bouton du rail — ICÔNE SEULE : le nom du module apparaît en infobulle à
   droite au survol/focus (décision 2026-08-15, « façon Grok »), rendue en
   PORTAIL (document.body, `position: fixed`) — même pattern qu'InfoTooltip.js :
   le rail défile (`overflow-y: auto`), une bulle positionnée en absolu à
   l'intérieur serait tronquée. Composant à part (pas une fonction) : la
   position s'appuie sur `getBoundingClientRect`, donc sur un ref par bouton. */
function RailButton({ label, icon, onClick, isActive, extra, className = '', onHoverStart, showTip = true, dataTour }) {
  const [tip, setTip] = useState(null);
  const ref = useRef(null);
  const show = () => {
    if (onHoverStart) onHoverStart();
    if (!showTip) return;          // le flyout porte déjà le nom du module
    const r = ref.current?.getBoundingClientRect();
    if (r) setTip({ top: r.top + r.height / 2, left: r.right + 8 });
  };
  const hide = () => setTip(null);
  return (
    <button
      ref={ref} className={`sb-railbtn${isActive ? ' is-active' : ''}${className ? ' ' + className : ''}`} onClick={onClick}
      data-tour={dataTour}
      onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}
      aria-label={label}
    >
      {icon}
      {extra}
      {tip && createPortal(
        <span className="sb-tip" style={{ top: tip.top, left: tip.left, transform: 'translateY(-50%)' }}>
          {label}
        </span>,
        document.body,
      )}
    </button>
  );
}

/* Pastille CLIENT en tête de capsule (décision 2026-08-15) : le client est un
   contexte GLOBAL — comme l'année — affiché UNE fois, plus répété dans chaque
   menu de module. Initiales du client ; clic → popover de changement quand
   l'utilisateur a plusieurs clients. Même mécanique portail que UserMenu. */
/* Menu UTILISATEUR (façon Grok, décision 2026-08-15) : la pastille d'initiales
   du pied de rail ouvre un popover — Paramètres · Administration · Déconnexion
   — au lieu d'empiler ces entrées en icônes. Rendu en PORTAIL (comme .sb-tip) :
   le rail défile et rognerait un popover positionné en absolu. Ouvre vers le
   HAUT (la pastille est en bas d'écran). Fermé au clic extérieur / Échap. */
/* (Le bouton export de la vue courante a quitté le rail le 2026-08-18 : il vit
   dans la barre supérieure, à droite de l'exercice — voir AppTopBar. Le
   registre pageExports reste la source unique des formats déclarés.) */

function UserMenu({ userEmail, userRoleLabel, items, t, locale, setLocale, buttonContent, buttonClassName, buttonTitle }) {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const open = () => {
    const r = btnRef.current?.getBoundingClientRect();
    // Décalé À DROITE de la capsule (2026-08-16), comme le popover client —
    // il ne recouvre plus la pile.
    if (r) setPos({ bottom: window.innerHeight - r.bottom, left: r.right + 10 });
  };
  React.useEffect(() => {
    if (!pos) return undefined;
    const close = (e) => { if (!e.target.closest('.sb-usermenu, .sb-identity__btn')) setPos(null); };
    const esc = (e) => { if (e.key === 'Escape') setPos(null); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [pos]);
  if (!userEmail && !items.length) return null;
  return (
    <>
      <button
        type="button" ref={btnRef} className={buttonClassName || 'sb-identity__btn'}
        title={buttonTitle || (userRoleLabel ? `${userEmail} — ${userRoleLabel}` : userEmail)}
        aria-haspopup="menu" aria-expanded={!!pos}
        onClick={() => (pos ? setPos(null) : open())}
      >
        {buttonContent || <span className="sb-identity__initials" aria-hidden>{(userEmail || '??').slice(0, 2).toUpperCase()}</span>}
      </button>
      {pos && createPortal(
        <div className="sb-usermenu" role="menu" style={{ bottom: pos.bottom, left: pos.left }}>
          {userEmail && (
            <div className="sb-usermenu__id">
              <b>{userEmail}</b>
              {userRoleLabel && <span>{userRoleLabel}</span>}
            </div>
          )}
          {items.map((it) => (
            <button key={it.key} type="button" role="menuitem" className="sb-usermenu__item"
              onClick={() => { setPos(null); it.onClick(); }}>
              {it.icon}{it.label}
            </button>
          ))}
          {/* Langue : préférence PERSONNELLE → sa place est ici, avec les autres
              réglages de la personne (sortie du menu de navigation 2026-08-15). */}
          {setLocale && (
            <div className="sb-usermenu__lang">
              <div className="sb-lang" role="group" aria-label="Langue">
                {['fr', 'en'].map((l) => (
                  <button key={l} type="button"
                    className={`sb-lang__b${locale === l ? ' is-active' : ''}`}
                    onClick={() => setLocale(l)}>{l.toUpperCase()}</button>
                ))}
              </div>
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

// Marqueurs de niveau de sous-item : crayon = saisie, engrenage = réglage.
const MARK = {
  pencil: <RiPencilLine className="sb-sub-ic" aria-hidden="true" />,
  gear: <Settings className="sb-sub-ic sb-sub-ic--stroke" aria-hidden="true" />,
  diamond: <Diamond size={9} fill="currentColor" stroke="none" aria-hidden="true" />,
  // Panneau interdit : un écran qu'on garde encore, mais qu'on ne prend plus.
  ban: <RiProhibitedLine className="sb-sub-ic" aria-hidden="true" />,
};

/* Marqueur d'un sous-item selon le proto : réglage (engrenage) · saisie (crayon)
   · stratégie (losange) · étape numérotée (pastille) · total (pastille Σ). */
function subMark(item, isInput) {
  if (item.mark === 'none') return null;
  if (item.mark === 'diamond') return <span className="sb-sub-mark sb-sub-mark--dia" aria-hidden>{MARK.diamond}</span>;
  if (item.mark === 'ban') return <span className="sb-sub-mark" aria-hidden>{MARK.ban}</span>;
  if (item.mark === 'sigma') return <span className="sb-sub-mark sb-sub-num sb-sub-num--sig" aria-hidden>Σ</span>;
  if (item.num != null) return <span className="sb-sub-mark sb-sub-num" aria-hidden>{item.num}</span>;
  if (item.setting) return <span className="sb-sub-mark" aria-hidden>{MARK.gear}</span>;
  if (isInput || item.input) return <span className="sb-sub-mark" aria-hidden>{MARK.pencil}</span>;
  return null;
}

export default function Sidebar({ page, setPage, onGoCashTab, cashTab, pinned, onTogglePin,

                                  activeTenant, onLogout,
                                  nav = NAV, lockedModules = [], isPlatformAdmin = false, canManageSettings = false, isTenantAdmin = false,
                                  canExportData = false,
                                  userEmail = null, userName = null, userRoleLabel = null,
                                  settingsTab, onGoSettingsTab, adminTab, onGoAdminTab,
                                  scopes = null, cockpitTab, onGoCockpitTab,
                                  }) {
  const { t } = useTranslation();
  const p = useP();
  const { locale, setLocale } = useLocale();
  /* Panneau DÉTACHÉ (docked=false) : il ne pousse plus le contenu, il flotte
     au-dessus et n'apparaît qu'au survol de la navigation — la table garde sa
     largeur en permanence. Cet état de survol est volontairement local et non
     persisté : seule la décision ancré/détaché se retient. */
  const [peek, setPeek] = React.useState(false);
  /* LE MENU EST UNE LIGNE (décision 2026-09-18). Les modules vivent dans la
     barre supérieure, le panneau porte les sections du module ouvert. Deux
     autres dispositions ont vécu deux jours à côté, un rail vertical et un
     arbre dépliant, chacune au choix de l'utilisateur : trois fois la même
     navigation à tenir juste, pour un choix que personne ne refait deux fois.
     Elles sont décrites et retrouvables dans `docs/archive/menu-rail-et-arbre.md`. */
  /* La ligne des modules s'affiche DANS la barre supérieure, à la place de la
     marque (2026-09-16) : elle y occupe la bande du menu, et le nom du module
     retrouve dessous le centre vertical du titre de page. Le nœud d'accueil
     est créé par AppTopBar, donc au commit suivant : on le cherche dans un
     effet, jamais au rendu. */
  const [modSlot, setModSlot] = React.useState(null);
  const [opsSlot, setOpsSlot] = React.useState(null);
  const [userSlot, setUserSlot] = React.useState(null);
  const [openMod, setOpenMod] = React.useState(null);
  /* Guide « Prise en main » : une étape peut demander l'ouverture du panneau
     (l'overlay du guide bloque le survol qui l'ouvrirait normalement). */
  const [tourOpen, setTourOpen] = React.useState(false);
  React.useEffect(() => {
    const onTour = (e) => setTourOpen(!!(e.detail && e.detail.open));
    window.addEventListener('palestre:tour-panel', onTour);
    return () => window.removeEventListener('palestre:tour-panel', onTour);
  }, []);
  /* Le panneau SE REPLIE de nouveau en disposition ligne (2026-09-17) : les
     modules vivent dans la barre, ils restent donc là quand les sections se
     cachent. Replié, une bande de dix pixels au bord gauche le fait revenir au
     survol, posé AU-DESSUS du contenu — la page ne se déplace pas.
     */
  /* MENU OUVERT À LA MAIN (demande d'Olivier, 2026-10-12) : la languette du
     filet l'ouvre par-dessus la page ; il reste ouvert jusqu'à un nouveau clic
     sur elle ou jusqu'au choix d'une section. Épinglé, il est ouvert de
     lui-même : l'ouverture à la main retombe. */
  const [menuOpen, setMenuOpen] = React.useState(false);
  React.useEffect(() => { if (pinned) setMenuOpen(false); }, [pinned]);
  const hover = !pinned && (peek || menuOpen);
  const panelOpen = tourOpen || pinned || peek || menuOpen;
  const onEdgeTab = () => {
    if (menuOpen) { setMenuOpen(false); setPeek(false); return; }
    setMenuOpen(true);
  };
  /* ⚠️ LES PLACES SE REPRENNENT À CHAQUE RENDU (2026-09-26) : elles étaient
     cherchées une fois, à l'ouverture du panneau. Si la barre supérieure n'est
     pas encore montée à cet instant (ordre de montage, rechargement à chaud),
     le nœud vaut `null` et la LIGNE DES MODULES ne revient jamais : la barre
     reste vide jusqu'au rechargement complet. Reposer le même nœud ne provoque
     aucun rendu, React compare par identité. */
  React.useLayoutEffect(() => {
    setModSlot(document.getElementById('lk-modslot'));
    setOpsSlot(document.getElementById('lk-opsslot'));
    setUserSlot(document.getElementById('lk-userslot'));
  });
  const labelOf = (node) => (node.labelKey ? t(node.labelKey) : node.label);

  // Panneau contextuel : visible quand la barre est DÉPLIÉE (`pinned`), point.
  // Le rail d'icônes reste toujours visible.

  function go(item) {
    if (item.kind === 'cash') { onGoCashTab ? onGoCashTab(item.tab) : setPage('treasury'); }
    else if (item.kind === 'cockpit') { onGoCockpitTab ? onGoCockpitTab(item.tab) : setPage('home'); }
    else if (item.kind === 'settings') { setPage('settings-hub'); onGoSettingsTab && onGoSettingsTab(item.tab); }
    else if (item.kind === 'admin') { setPage('admin-hub'); onGoAdminTab && onGoAdminTab(item.tab); }
    else setPage(item.key);
  }
  function itemActive(item) {
    if (item.kind === 'cash') return page === 'treasury' && cashTab === item.tab;
    if (item.kind === 'cockpit') return (page === 'home' || page === 'cockpit') && cockpitTab === item.tab;
    if (item.kind === 'settings') return page === 'settings-hub' && settingsTab === item.tab;
    if (item.kind === 'admin') return page === 'admin-hub' && adminTab === item.tab;
    return page === item.key;
  }
  // 1ʳᵉ page navigable d'un module.
  function firstItem(node) {
    if (node.railGroup) {
      const g = node.groups.find(x => x.id === node.railGroup);
      if (g && g.items.length) return g.items[0];
    }
    if (node.items && node.items.length) return node.items[0];
    if (node.groups) { const g = node.groups.find(g => g.items && g.items.length); return g ? g.items[0] : null; }
    return null;
  }

  // Trois niveaux de sous-item : lecture (défaut), saisie ✎, réglage (engrenage).
  function renderItems(items, isInput, tone) {
    return items.filter(item => !item.hidden).map(item => {
      const kind = item.setting ? 'setting' : (isInput || item.input) ? 'input' : 'reading';
      return (
        <button
          key={item.key || item.labelKey || item.label}
          className={`sb-sub sb-sub--${kind}${item.off ? ' sb-sub--off' : ''}${tone ? ` sb-sub--tone-${tone}` : ''}${item.muted ? ' sb-sub--muted' : ''}${item.strong ? ' sb-sub--strong' : ''}${item.danger ? ' sb-sub--danger' : ''}${item.sub ? ' sb-sub--child' : ''}${item.subLast ? ' sb-sub--child-last' : ''}${itemActive(item) ? ' is-active' : ''}`}
          onClick={() => { if (!item.off) { go(item); setMenuOpen(false); } }}
          aria-disabled={item.off || undefined}
          title={item.off ? t('nav:needsFinance', 'Demande le module Finance') : undefined}
        >
          {subMark(item, isInput)}
          <span className="sb-lbl">{labelOf(item)}</span>
          {/* Le cadenas dit ce qui dépend de la connexion à Finance (2026-09-28),
              et les sections réservées des Paramètres (2026-10-11). */}
          {item.lock && <Lock size={11} className="sb-sub-h__ic" aria-hidden />}
          {item.tag && <span className="sb-sub-tag">{item.tag}</span>}
        </button>
      );
    });
  }

  // Un nœud du rail → contenu de panneau (le Cockpit fabrique ses sections
  // depuis la config du client ; les bandes portent déjà items/groups).
  function panelOf(node) {
    if (!node) return null;
    if (node.type === 'page' && node.id === 'home') {
      /* L'ACCUEIL ouvre le panneau (2026-09-16) : la page que tout le monde a,
         quels que soient les modules vendus. Les vues du Cockpit suivent,
         filtrées par la config du client et les droits de lecture. */
      const tabs = visibleCockpitTabs(activeTenant?.cockpit_tabs, scopes, activeTenant?.enabled_modules || []);
      const toItem = (s) => ({ labelKey: s.labelKey, kind: 'cockpit', tab: s.id });
      /* Le JOURNAL et les DÉCISIONS montent AVEC la page d'accueil (2026-09-18) :
         ce qui a été observé et décidé se lit avant les chiffres qui l'ont
         motivé, et ce ne sont pas des vues d'analyse. Les vues, elles, forment
         le groupe COCKPIT : « Accueil · Accueil · Rentabilité » ne disait pas
         où s'arrête la page d'accueil et où commence le diagnostic. */
      const aside = tabs.filter((s) => s.aside).map(toItem);
      const views = tabs.filter((s) => !s.aside).map(toItem);
      /* ORDRE DU PROTOTYPE (2026-10-01) : Accueil, Journal, Décisions ; puis
         les vues du COCKPIT ; puis les RESSOURCES, Guides, Fiches (2026-10-09)
         et Nouveautés, qui aident à se servir de l'application sans en être le
         contenu. */
      return { ...node, groups: [
        { items: [{ labelKey: 'nav:welcome', key: 'welcome' }, ...aside] },
        ...(views.length ? [{ headKey: 'nav:groups.direction', items: views }] : []),
        { headKey: 'nav:groups.resources', items: [
          { labelKey: 'nav:welcomeGuides', key: 'welcome-guides' },
          { labelKey: 'nav:welcomeFiches', key: 'welcome-fiches' },
          { labelKey: 'nav:welcomeNews', key: 'welcome-news' },
        ] },
      ] };
    }
    return node;
  }
  // Module actif → contenu du panneau (dérivé de la page courante).
  /* Les deux hubs d'administration comme NŒUDS de menu : le panneau les monte
     quand on y est, et l'arbre les déplie à côté des modules (2026-09-17). */
  function adminNode() {
    const toItem = (sec) => ({ labelKey: sec.labelKey, kind: 'admin', tab: sec.id, danger: sec.danger });
    return { id: 'admin-hub', labelKey: 'nav:admin', icon: I.admin, groups: [
      ...opsGroups(nav),
      { headKey: 'admin:menu.platform',
        items: ADMIN_SECTIONS.filter((sec) => sec.group === 'platform').map(toItem) },
      /* DONNÉES CLIENTS (2026-10-11) : une seule section, nommée par le client
         actif, à la place du bouton Gouvernance client. */
      { headKey: 'admin:menu.clientData',
        items: ADMIN_SECTIONS.filter((sec) => sec.group === 'client')
          .map((sec) => ({ label: activeTenant?.name || '?', kind: 'admin', tab: sec.id })) },
    ] };
  }

  // La bande de la page ; pour une section commune, celle d'où l'on vient.
  const pageBand = bandForPage(nav, page);
  function activeModule() {
    // Une page de la bande servie AILLEURS (Journal, Nouveautés) ne réveille pas
    // sa bande : c'est l'Administration qui la porte, plus bas.
    const b = opsKeys(nav).includes(page) ? null : pageBand;
    // Bande à groupe de rail : son panneau ne montre que ce groupe.
    if (b && b.railGroup) return { ...b, groups: b.groups.filter(g => g.id === b.railGroup) };
    if (b) return b;
    if (page === 'settings-hub') return { ...SETTINGS, items:
      SETTINGS_SECTIONS
        // Les taux de charges sociales : administrateur du client et
        // administrateur Palestre seulement, quels que soient les modules
        // (2026-09-28). Les Données : qui lit People et Finance (2026-10-11).
        .filter(s => s.id !== 'social' || isTenantAdmin)
        .filter(s => s.id !== 'data' || canExportData)
        // Le cadenas marque les sections réservées (2026-10-11).
        .map(s => ({ labelKey: s.labelKey, kind: 'settings', tab: s.id, lock: s.lock })) };
    // Administration : EXPLOITATION, PLATEFORME (tous les clients), puis
    // DONNÉES CLIENTS, dont l'entrée porte le NOM du client actif : l'écran dit
    // en permanence sur qui il opère (incident 2026-08-18).
    if (page === 'admin-hub' || opsKeys(nav).includes(page)) return adminNode();
    // Cockpit : ses vues (ex-onglets) sont des sections du panneau, filtrées par
    // la config du client (cockpit_tabs) et les droits de lecture de la personne.
    // On vise la page 'home' NOMMÉMENT : depuis l'Accueil (2026-09-16), la
    // première page du menu n'est plus le Cockpit.
    return panelOf(nav.find(n => n.type === 'page' && n.id === 'home'));
  }
  // Le panneau montre TOUJOURS le module actif (déduit de la page courante).
  // Le survol n'ouvre plus rien (décision 2026-08-18) : la barre est ANCRÉE et
  // pousse le contenu — s'élargir au passage de la souris déplaçait la page
  // sous les yeux du lecteur. L'ouverture se fait au clic.
  const active = activeModule();
  /* L'arbre ne liste que les modules. Les adresses de l'exploitant
     (Administration, Atelier) et les Paramètres n'y figurent pas :
     leurs SECTIONS s'affichent donc comme dans les autres dispositions, à la
     place de l'arbre, sinon le panneau n'aurait plus rien à montrer
     (constaté le 2026-09-17). */

  /* LA PUNAISE SEULE GOUVERNE LE PANNEAU (demande d'Olivier, 2026-10-03).
     Changer de module ÉPINGLAIT le panneau s'il était libre, et enregistrait ce
     choix : le menu passait de libre à figé selon le chemin suivi. Le module
     change, l'état de la punaise reste celui qu'on a posé. */

  // Sélection depuis la barre des modules : navigue vers la 1ʳᵉ page du module.
  function selectNode(node) {
    /* La maison ouvre l'ACCUEIL, la première section de son panneau ; le
       Cockpit se rejoint par ses vues, juste dessous (2026-09-16). */
    if (node.type === 'page') { setPage(node.id === 'home' ? 'welcome' : node.id); return; }
    const it = firstItem(node);
    if (it) go(it);
  }
  function nodeActive(node) {
    if (node.id === 'admin-hub') return page === 'admin-hub' || opsKeys(nav).includes(page);
    if (node.type === 'page') return node.id === 'home' ? (page === 'home' || page === 'cockpit' || page.startsWith('welcome')) : page === node.id;
    if (node.railGroup) return railGroupKeys(nav).includes(page);
    return pageBand ? pageBand.id === node.id : bandKeys(node).includes(page);
  }


  /* Ouverture des deux hubs d'administration : une seule décision, partagée
     par le rail et par la ligne haute — sinon les deux dispositions finissent
     par atterrir sur des pages différentes. */
  function openAdminHub() {
    const first = opsKeys(nav)[0];
    if (first) { setPage(first); return; }
    setPage('admin-hub');
    onGoAdminTab && onGoAdminTab(ADMIN_SECTIONS.find(s => s.group === 'platform').id);
  }


  /* DEUX BOUTONS, DEUX ADRESSES (2026-10-11) : l'Administration s'ouvre
     directement, l'Atelier a le sien à côté. Ils partageaient un menu
     déroulant à trois entrées (2026-09-17), avec la Gouvernance client,
     devenue une section de l'Administration. */
  function opsButtons() {
    const entries = [
      { id: 'admin-hub', label: t('nav:admin'), icon: I.admin,
        active: page === 'admin-hub' || opsKeys(nav).includes(page), go: openAdminHub },
      ...nav.filter((n) => n.adminOnly).map((node) => ({
        id: node.id, label: labelOf(node), icon: node.icon,
        active: nodeActive(node), go: () => selectNode(node),
      })),
    ];
    return entries.map((e) => (
      <button key={e.id} type="button"
              className={`app-topbar__icon${e.active ? ' is-active' : ''}`}
              onClick={e.go} title={e.label} aria-label={e.label}
              aria-current={e.active ? 'page' : undefined}>
        {e.icon}
      </button>
    ));
  }

  /* Bouton de module de la LIGNE HAUTE : ICÔNE SEULE, l'actif reconnu à sa
     pilule claire. Le nom du module vit en titre, juste dessous, en 18 px : le
     porter aussi dans la pilule le disait deux fois et faisait déborder la
     ligne — sept modules dont « Analytique Services » ne tiennent pas dans la
     largeur d'un panneau. Le survol donne le nom des six autres. */
  /* Un module dans la BARRE : icône puis nom, l'un sous l'autre (2026-09-17).
     La barre a la largeur pour les écrire, ce que la bande du menu n'avait
     pas ; l'infobulle part donc, elle ne dirait que ce qui est déjà lu. */
  /* UN MODULE ACHETÉ MAIS FERMÉ AU RÔLE (2026-10-12) : à sa place dans la ligne,
     grisé, un cadenas après son nom, et rien ne s'ouvre. Il se lit comme les
     sections réservées de Paramètres : on voit qu'il existe et pourquoi on n'y
     entre pas, au lieu d'une erreur à l'ouverture. */
  function lockedModBtn(node) {
    const label = labelOf(node);
    return (
      <button key={node.id} type="button" className="sb-railbtn sb-topmod sb-topmod--locked" aria-disabled="true"
              aria-label={`${label} · ${p('Votre rôle n’a pas accès à ce module')}`}
              title={p('Votre rôle n’a pas accès à ce module')} onClick={(e) => e.preventDefault()}>
        {node.icon}
        <span className="sb-topmod__cap">{label}</span>
        <Lock size={11} aria-hidden />
      </button>
    );
  }
  function topModBtn(node) {
    return (
      <RailButton key={node.id} label={labelOf(node)} icon={node.icon} showTip={false}
        onClick={() => selectNode(node)} isActive={nodeActive(node)}
        className={`sb-topmod${node.sep ? ' sb-topmod--sep' : ''}`}
        extra={<span className="sb-topmod__cap">{labelOf(node)}</span>}
        dataTour={`band-${node.id}`} />
    );
  }


  /* Pied du panneau : identité en clair, à la hauteur de la pastille du rail.
     La langue (préférence personnelle) l'accompagne — elle vivait dans le
     popover supprimé. */
  /* PIED — une seule zone, trois rangs de poids décroissant (2026-09-17) : ce
     qu'on ouvre (les adresses de l'exploitant), qui l'on est, puis la langue.
     Sans rail, la pastille d'initiales n'a plus personne pour la porter : elle
     ouvre donc la ligne d'identité, et la langue la ferme à droite. */
  /* PIED SANS RAIL (refonte 2026-09-17, d'après la maquette d'Olivier) : le
     pied ne montre plus que les adresses de l'exploitant et QUI l'on est. Les
     réglages — présentation du menu, langue — et la déconnexion vivent dans la
     carte que la ligne d'identité ouvre : on les règle une fois, ils n'ont pas
     à occuper le menu en permanence. */
  const [userOpen, setUserOpen] = React.useState(false);
  const firstName = (userName || '').trim().split(/\s+/)[0]
    || (userEmail || '').split('@')[0];

  /* LA CARTE SE FERME AU CLIC EXTÉRIEUR ET À ÉCHAP (2026-10-16) : elle ne se
     refermait qu'en recliquant sur la pastille. La liste des langues est rendue
     en portail hors de la carte : un clic dessus ne ferme rien. */
  React.useEffect(() => {
    if (!userOpen) return undefined;
    const close = (e) => { if (!e.target.closest('.sb-panel-foot, [role="listbox"]')) setUserOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setUserOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [userOpen]);

  /* La carte utilisateur sert les TROIS dispositions (2026-09-17) : elle ne
     vivait que là où il n'y a pas de rail, or c'est elle qui porte le choix de
     disposition. Une fois passé en Rail, plus rien ne permettait d'en sortir. */
  const panelFoot = userEmail ? (
    <div className="sb-panel-foot sb-panel-foot--wide">
      {userOpen && (
        <div className="sb-ucard">
          <div className="sb-ucard__mail" title={userEmail}>{userEmail}</div>
          {setLocale && (
            <div className="sb-uset">
              <div className="sb-uset__l">{t('nav:language', 'Langue')}</div>
              <Listbox value={locale} onChange={setLocale} className="sb-ulang"
                options={[{ value: 'fr', label: 'Français' }, { value: 'en', label: 'English' }]} />
            </div>
          )}
          {onLogout && (
            <button type="button" className="sb-uout" onClick={onLogout}>
              {I.logout}{t('nav:logout')}
            </button>
          )}
        </div>
      )}
      <button type="button" className={`sb-urow${userOpen ? ' is-open' : ''}`}
              aria-expanded={userOpen} onClick={() => setUserOpen((v) => !v)}>
        <span className="sb-foot-av" aria-hidden>{(userEmail || '??').slice(0, 2).toUpperCase()}</span>
        <span className="sb-foot-id">
          <span className="sb-panel-user">{firstName}</span>
          {userRoleLabel && <span className="sb-panel-role">{userRoleLabel}</span>}
        </span>
        <ChevronRight size={14} strokeWidth={2} className="sb-urow__chev" aria-hidden="true" />
      </button>
      {/* LA DÉCONNEXION SE VOIT AU REPOS, EN PIED DE MENU (décision d'Olivier,
          2026-09-24). Elle s'était repliée dans la carte d'identité le 17.09 :
          il fallait savoir que son nom s'ouvrait pour sortir. Elle revient
          sous la ligne d'identité, tout en bas, donc loin de ce qu'on clique
          souvent : visible sans être une fausse manœuvre en puissance. Dans
          la barre supérieure, c'est la carte qui la porte, et la CSS n'en
          montre qu'une à la fois. */}
      {onLogout && (
        <button type="button" className="sb-uout sb-uout--foot" onClick={onLogout}>
          {I.logout}{t('nav:logout')}
        </button>
      )}
    </div>
  ) : null;

  return (
    <>
    <aside
      className={`sb sb--topline${panelOpen ? ' sb--pinned' : ''}${hover ? ' sb--float' : ''}`}
      onMouseEnter={() => setPeek(true)}
      onMouseLeave={() => setPeek(false)}
    >
      {/* ── Colonne 1 : rail d'icônes (absent en variante « ligne haute », où
             les modules vivent en tête du panneau) ── */}
      {!pinned && (
        <div className="sb-hot" aria-hidden onMouseEnter={() => setPeek(true)} />
      )}

      {/* ── Colonne 2 : panneau contextuel du module actif ── */}
      <div className="sb-panel-col">
        {/* En-tête = la MARQUE seule, à la hauteur du logo du rail, fermée par
            le filet (2026-08-18). Le nom du module vient SOUS ce filet. */}
        {/* La MARQUE vit dans la barre supérieure pleine largeur depuis le
            2026-08-21 — le panneau commence directement par le module. */}
        {/* En-tête du panneau : le nom du module, et la PUNAISE. L'étoile
            « module socle » et la croix de fermeture ont été retirées le
            2026-08-20 — la croix disait « fermer » sans dire « et après ? »,
            l'étoile n'était lue par personne. */}
        {/* LIGNE DES MODULES (essai 2026-09-16) : elle remplace le rail, en tête
            du panneau. Les modules réservés à l'exploitant n'y figurent pas —
            leur nombre varie selon le compte, la ligne changerait de longueur
            d'un utilisateur à l'autre. Ils tiennent le pied du panneau. */}
        {modSlot && createPortal(
          <div className="sb-topmods">
            {NAV.filter(n => !n.adminOnly).map((def) => {
              const open = nav.find(n => n.id === def.id);
              if (open) return topModBtn(open);
              return lockedModules.includes(def.id) ? lockedModBtn(def) : null;
            })}
          </div>,
          modSlot,
        )}
        {userSlot && createPortal(panelFoot, userSlot)}
        {opsSlot && isPlatformAdmin && createPortal(opsButtons(), opsSlot)}
        {/* (Le badge d'environnement a rejoint la barre, contre le symbole, le
            2026-09-17 : il qualifie l'instance, pas le module ouvert.) */}
        {/* (Le RETOUR a quitté le panneau pour la fenêtre principale le
            2026-09-29 : voir AppShell.) */}
        <div className="sb-panel-modrow">
          <span className="sb-panel-title">{active ? labelOf(active) : ''}</span>
          {/* FIGÉ ou LIBRE : une PUNAISE au bout de la ligne du module
              (2026-09-17, retour d'Olivier le soir même). Le bouton ne FERME
              pas le menu, il le libère : libre, le panneau revient au survol du
              bord et se retire dès qu'on le quitte. L'icône panneau-fermer
              annonçait donc une fermeture qui ne se voyait qu'en sortant la
              souris, si bien que le clic passait pour sans effet. La punaise
              dit l'état, et son changement d'aplat donne au geste sa réponse
              immédiate. Il ne s'affiche que là où le menu peut se replier sans
              faire disparaître la navigation : l'arbre n'en a pas. */}
          <button type="button" className={`sb-panel-fold${pinned ? ' is-pinned' : ''}`} onClick={onTogglePin}
                  title={pinned ? t('nav:panelUnpin') : t('nav:panelPin')}
                  aria-label={pinned ? t('nav:panelUnpin') : t('nav:panelPin')}
                  aria-pressed={!!pinned}>
            {pinned
              ? <RiPushpinLine size={16} aria-hidden="true" />
              : <RiUnpinLine size={16} aria-hidden="true" />}
          </button>
        </div>
        {/* Toutes les sections se lisent EN HAUT, sous le nom du module —
            Administration comprise depuis que son icône a quitté le pied du
            rail (2026-08-26) : l'alignement en bas ne répondait plus à rien. */}
        <div className="sb-panel-body">
          {/* ARBRE (essai 2026-09-16) : un module = un entête qui se déplie sur
              ses sections. On voit d'un coup ce que porte le module d'à côté
              sans quitter la page ouverte — déplier ne navigue pas, seule une
              section navigue. */}
          {active && active.items && renderItems(active.items)}
          {/* Modules à étapes groupées (ex. Planification) : tête de groupe en
              petites capitales + sections — mêmes boutons que les items plats. */}
          {active && active.groups && active.groups.map((g, gi) => (
            <div className={`sb-group${g.variant ? ` sb-group--${g.variant}` : ''}`} key={g.headKey || g.head || gi}>
              {(g.headKey || g.head) && (
                <div className="sb-sub-h">
                  {g.headKey ? t(g.headKey) : g.head}
                  {g.headMark === 'pencil' && <span className="sb-sub-h__ic" aria-hidden>{MARK.pencil}</span>}
                  {/* Le cadenas dit qu'un périmètre garde le groupe (2026-09-27). */}
                  {g.lock && <Lock size={11} className="sb-sub-h__ic" aria-hidden />}
                </div>
              )}
              {renderItems(g.items, undefined, g.tone)}
            </div>
          ))}
        </div>
        {/* PIED DU PANNEAU sans rail — QUATRE rangs, du réglage à la session
            (2026-09-17) : la disposition, la langue, les adresses de
            l'exploitant, puis qui l'on est. Les deux réglages portent le même
            costume, libellé en petites capitales à gauche, choix à droite ;
            ils ne se mélangent plus aux icônes. */}

      </div>

    </aside>
    {/* LA LANGUETTE DU FILET (proposition B, 2026-10-12) : collée au filet du
        menu, à mi-hauteur, elle ouvre et ferme le menu et suit son bord. Elle
        est posée HORS de l'aside, sinon le survol de la languette ouvrirait le
        menu avant le clic et la ferait sauter de place. Épinglé, le menu est déjà
        ouvert et seule la punaise le libère : la languette se retire. */}
    <button type="button" className={`sb-edge-tab${panelOpen ? ' is-open' : ''}${pinned ? ' is-hidden' : ''}`} onClick={onEdgeTab}
            aria-hidden={pinned || undefined} tabIndex={pinned ? -1 : undefined}
            aria-expanded={panelOpen} title={panelOpen ? p('Fermer le menu') : p('Ouvrir le menu')}
            aria-label={panelOpen ? p('Fermer le menu') : p('Ouvrir le menu')}>
      {panelOpen ? <RiArrowLeftSLine size={16} aria-hidden="true" /> : <RiArrowRightSLine size={16} aria-hidden="true" />}
    </button>
    </>
  );
}
